import {
  api,
  ApiError,
  type KeyDirectoryEntry,
  type SharedFolderGuestInviteSummary,
  type SharedFolderMember,
} from '../api/client';
import { getAppState } from '../app/index';
import {
  createSharedFolderGuestInvite,
  createSharedFolderGuestInviteMaterials,
  type SharedFolderGuestPermission,
} from '../crypto/sharedFolderGuestInvite';
import { normalizeGuestRootPath } from '../crypto/sharedFolderGuestRoot';
import type { SharedFolderManifestPlaintext } from '../crypto/sharedFolderManifest';
import {
  inviteSharedFolderMember,
  type SharedFolderMemberPermission,
} from '../crypto/sharedFolderMemberInvite';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import { decodeBase64Url } from '../crypto/primitives';
import { sealQuickShareFragmentPackage } from '../crypto/quickSharePackage';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { formatBytes } from '../i18n/formatBytes';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { enhanceDataTableCards } from './dataTableCards';
import { iconImg } from './icons';
import { showConfirmDialog } from './confirmDialog';
import { showFolderGuestLinkDialog } from './folderGuestLinkDialog';
import { bindModalDismiss, createModalShell } from './modalDialogShell';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from './passwordPolicyUi';
import { connectQuickShareOwner } from '../ws/quickShare';

export type FolderAccessRowKind = 'user' | 'guest' | 'quickShare';

export interface FolderAccessRow {
  id: string;
  kind: FolderAccessRowKind;
  name: string;
  permission: string;
  active: boolean;
  sortName: string;
}

export function buildFolderAccessRows(
  members: SharedFolderMember[],
  guests: SharedFolderGuestInviteSummary[],
  untitledGuest: string,
  quickShareName: string,
): FolderAccessRow[] {
  const rows: FolderAccessRow[] = [
    ...members.map((member) => ({
      id: member.userId,
      kind: 'user' as const,
      name: member.displayName,
      permission: member.permission,
      active: true,
      sortName: member.displayName.toLocaleLowerCase(),
    })),
    ...guests.map((guest) => {
      const isQuickShare = guest.inviteKind === 'QuickShare';
      const name = isQuickShare
        ? quickShareName
        : (guest.label?.trim() || untitledGuest);
      return {
        id: guest.id,
        kind: (isQuickShare ? 'quickShare' : 'guest') as FolderAccessRowKind,
        name,
        permission: guest.permission,
        active: !guest.revoked,
        sortName: name.toLocaleLowerCase(),
      };
    }),
  ];

  rows.sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }
    return a.sortName.localeCompare(b.sortName);
  });
  return rows;
}

function tMemberPermission(permission: string): string {
  switch (permission) {
    case 'Full':
      return t('files.owner.accessFull');
    case 'ReadWrite':
      return t('files.owner.accessReadWrite');
    case 'Read':
      return t('files.owner.accessRead');
    default:
      return permission;
  }
}

function tAccessType(kind: FolderAccessRowKind): string {
  switch (kind) {
    case 'user':
      return t('files.owner.accessTypeUser');
    case 'quickShare':
      return t('files.owner.accessTypeQuickShare');
    default:
      return t('files.owner.accessTypeGuest');
  }
}

function mapInviteError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return t('status.error');
  }

  switch (error.code) {
    case 'invitee_not_eligible':
      return t('files.owner.inviteNotEligible');
    case 'member_already_exists':
      return t('files.owner.memberExists');
    case 'cannot_invite_self':
      return t('files.owner.cannotInviteSelf');
    case 'creator_must_remain_full':
      return t('files.owner.creatorMustStayFull');
    case 'cannot_remove_creator':
      return t('files.owner.cannotRemoveCreator');
    case 'invalid_permission':
      return t('files.owner.invalidPermission');
    case 'access_denied':
      return t('files.owner.accessDenied');
    case 'quick_share_already_active':
      return t('files.owner.quickShareAlreadyActive');
    default:
      return t('status.error');
  }
}

export interface SharedFolderAccessPanelOptions {
  folderId: string;
  folderKey: Uint8Array;
  manifest: SharedFolderManifestPlaintext;
  /** Called after a successful quick-share handoff (dialog already closed). */
  onQuickShareComplete?: () => void;
}

export async function renderSharedFolderAccessPanel(
  host: HTMLElement,
  options: SharedFolderAccessPanelOptions,
): Promise<() => void> {
  const state = getAppState();
  if (state.kind !== 'authenticated') {
    host.replaceChildren();
    return () => undefined;
  }

  const currentUserId = state.session.userId;
  const { folderId, folderKey, manifest, onQuickShareComplete } = options;

  host.innerHTML = `<p class="muted" data-i18n="status.loading"></p>`;
  applyI18n(host);

  let members: SharedFolderMember[] = [];
  let guestInvites: SharedFolderGuestInviteSummary[] = [];
  let directory: KeyDirectoryEntry[] = [];

  try {
    [members, guestInvites, directory] = await Promise.all([
      api.listSharedFolderMembers(folderId),
      api.listSharedFolderGuestInvites(folderId),
      api.getKeyDirectory(),
    ]);
  } catch (cause) {
    console.error('Load folder access failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return () => undefined;
  }

  const memberUserIds = new Set(members.map((member) => member.userId));
  let inviteCandidates = directory.filter(
    (entry) => !memberUserIds.has(entry.userId) && entry.userId !== currentUserId,
  );

  const panel = document.createElement('div');
  panel.className = 'files-access-panel';
  panel.innerHTML = `
    <div class="files-access-toolbar">
      <button type="button" class="button primary" id="files-access-invite-user" data-i18n="files.owner.inviteUser"></button>
      <button type="button" class="button ghost" id="files-access-invite-guest" data-i18n="files.owner.createGuestInvite"></button>
      <button type="button" class="button ghost" id="files-access-quick-share" data-i18n="files.owner.quickShare"></button>
    </div>
    <p class="error-text" id="files-access-error" hidden></p>
    <div class="files-table-wrap">
      <table class="data-table files-access-table">
        <thead>
          <tr>
            <th data-i18n="files.owner.memberName"></th>
            <th data-i18n="files.owner.accessType"></th>
            <th data-i18n="files.owner.permission"></th>
            <th data-i18n="files.owner.guestInviteStatus"></th>
            <th data-i18n="files.owner.guestUploadLimitsTitle"></th>
            <th class="data-table__actions-heading" data-i18n="files.owner.listActions"></th>
          </tr>
        </thead>
        <tbody id="files-access-body"></tbody>
      </table>
    </div>
  `;

  host.replaceChildren(panel);
  applyI18n(panel);

  const body = panel.querySelector('#files-access-body') as HTMLTableSectionElement;
  const errorEl = panel.querySelector('#files-access-error') as HTMLParagraphElement;
  let busy = false;

  const showError = (message: string) => {
    errorEl.hidden = false;
    errorEl.textContent = message;
  };

  const clearError = () => {
    errorEl.hidden = true;
    errorEl.textContent = '';
  };

  const formatInviteBudget = (invite: SharedFolderGuestInviteSummary): string => {
    if (invite.permission !== 'ReadWrite') {
      return '—';
    }

    const parts: string[] = [];
    const maxFiles = typeof invite.maxGuestUploadFiles === 'number' ? invite.maxGuestUploadFiles : null;
    const maxBytes = typeof invite.maxGuestUploadTotalBytes === 'number'
      ? invite.maxGuestUploadTotalBytes
      : null;
    if (maxFiles !== null) {
      parts.push(
        `${t('files.owner.guestUploadUsedFiles')}: ${invite.guestUploadFilesUsed} / ${maxFiles}`,
      );
    }
    if (maxBytes !== null) {
      parts.push(
        `${t('files.owner.guestUploadUsedSize')}: ${formatBytes(invite.guestUploadBytesUsed)} / ${formatBytes(maxBytes)}`,
      );
    }
    if (parts.length === 0) {
      return t('files.owner.guestUploadUnlimited');
    }
    return parts.join(' · ');
  };

  const renderRows = () => {
    body.replaceChildren();
    const rows = buildFolderAccessRows(
      members,
      guestInvites,
      t('files.owner.guestInviteUntitled'),
      t('files.owner.quickShare'),
    );
    if (rows.length === 0) {
      const empty = document.createElement('tr');
      empty.innerHTML = `<td colspan="6" class="muted" data-i18n="files.owner.accessEmpty"></td>`;
      body.append(empty);
      applyI18n(empty);
      return;
    }

    for (const row of rows) {
      const tr = document.createElement('tr');
      if (!row.active) {
        tr.classList.add('files-access-row--revoked');
      }

      const nameCell = document.createElement('td');
      if (row.kind === 'guest' || row.kind === 'quickShare') {
        const invite = guestInvites.find((entry) => entry.id === row.id);
        const rootLabel = invite?.rootPath
          ? t('files.owner.guestRootPathScoped', { path: invite.rootPath })
          : '';
        nameCell.textContent = rootLabel ? `${row.name} · ${rootLabel}` : row.name;
      } else {
        nameCell.textContent = row.name;
      }

      const typeCell = document.createElement('td');
      typeCell.textContent = tAccessType(row.kind);

      const permissionCell = document.createElement('td');
      const member = row.kind === 'user'
        ? members.find((entry) => entry.userId === row.id)
        : undefined;
      const guestInviteForPerm = row.kind === 'guest' || row.kind === 'quickShare'
        ? guestInvites.find((entry) => entry.id === row.id)
        : undefined;
      if (member && member.permission !== 'Full') {
        const select = document.createElement('select');
        select.className = 'files-manage-permission-select';
        for (const value of ['Read', 'ReadWrite', 'Full'] as const) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = tMemberPermission(value);
          select.append(option);
        }
        select.value = member.permission;
        select.addEventListener('change', () => {
          void (async () => {
            if (busy) {
              select.value = member.permission;
              return;
            }

            const previous = member.permission;
            const next = select.value as SharedFolderMemberPermission;
            busy = true;
            clearError();
            try {
              await api.patchSharedFolderMember(folderId, member.userId, next);
              member.permission = next;
              row.permission = next;
            } catch (cause) {
              select.value = previous;
              showError(mapInviteError(cause));
            } finally {
              busy = false;
            }
          })();
        });
        permissionCell.append(select);
      } else if (guestInviteForPerm && row.active) {
        const select = document.createElement('select');
        select.className = 'files-manage-permission-select';
        for (const value of ['Read', 'ReadWrite'] as const) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = tMemberPermission(value);
          select.append(option);
        }
        select.value = guestInviteForPerm.permission === 'ReadWrite' ? 'ReadWrite' : 'Read';
        select.addEventListener('change', () => {
          void (async () => {
            if (busy) {
              select.value = guestInviteForPerm.permission === 'ReadWrite' ? 'ReadWrite' : 'Read';
              return;
            }

            const previous = guestInviteForPerm.permission === 'ReadWrite' ? 'ReadWrite' : 'Read';
            const next = select.value as 'Read' | 'ReadWrite';
            busy = true;
            clearError();
            try {
              const updated = await api.patchSharedFolderGuestInvite(folderId, guestInviteForPerm.id, {
                permission: next,
                maxGuestUploadFiles: next === 'Read' ? null : guestInviteForPerm.maxGuestUploadFiles ?? null,
                maxGuestUploadTotalBytes: next === 'Read'
                  ? null
                  : guestInviteForPerm.maxGuestUploadTotalBytes ?? null,
              });
              const index = guestInvites.findIndex((entry) => entry.id === updated.id);
              if (index >= 0) {
                guestInvites[index] = updated;
              }
              row.permission = updated.permission;
              renderRows();
            } catch (cause) {
              select.value = previous;
              showError(mapInviteError(cause));
            } finally {
              busy = false;
            }
          })();
        });
        permissionCell.append(select);
      } else {
        permissionCell.textContent = tMemberPermission(row.permission);
      }

      const statusCell = document.createElement('td');
      if (row.kind === 'guest' || row.kind === 'quickShare') {
        statusCell.textContent = row.active
          ? t('files.owner.guestInviteActive')
          : t('files.owner.guestInviteRevoked');
      } else {
        statusCell.textContent = '—';
      }

      const budgetCell = document.createElement('td');
      const invite = row.kind === 'guest' || row.kind === 'quickShare'
        ? guestInvites.find((entry) => entry.id === row.id)
        : undefined;
      budgetCell.textContent = invite ? formatInviteBudget(invite) : '—';

      const actionsCell = document.createElement('td');
      actionsCell.className = 'data-table__actions';

      if (row.kind === 'user' && member && member.permission !== 'Full') {
        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'button ghost';
        removeButton.innerHTML = `<span data-i18n="files.owner.removeMember"></span>`;
        removeButton.addEventListener('click', () => {
          void (async () => {
            if (busy) {
              return;
            }

            const confirmed = await showConfirmDialog({
              titleKey: 'files.owner.removeMemberTitle',
              messageKey: 'files.owner.removeMemberConfirm',
              confirmLabelKey: 'files.owner.removeMember',
              confirmClassName: 'primary',
            });
            if (!confirmed) {
              return;
            }

            busy = true;
            clearError();
            try {
              await api.removeSharedFolderMember(folderId, member.userId);
              members = members.filter((entry) => entry.userId !== member.userId);
              memberUserIds.delete(member.userId);
              const entry = directory.find((item) => item.userId === member.userId);
              if (entry && entry.userId !== currentUserId) {
                inviteCandidates.push(entry);
              }
              renderRows();
            } catch (cause) {
              showError(mapInviteError(cause));
            } finally {
              busy = false;
            }
          })();
        });
        actionsCell.append(removeButton);
      } else if ((row.kind === 'guest' || row.kind === 'quickShare') && row.active && invite) {
        if (invite.permission === 'ReadWrite') {
          const editLimitsButton = document.createElement('button');
          editLimitsButton.type = 'button';
          editLimitsButton.className = 'button ghost';
          editLimitsButton.innerHTML = `<span data-i18n="files.owner.guestUploadLimitsEdit"></span>`;
          editLimitsButton.addEventListener('click', () => {
            void showEditGuestLimitsDialog({
              folderId,
              invite,
              onUpdated: (updated) => {
                const index = guestInvites.findIndex((entry) => entry.id === updated.id);
                if (index >= 0) {
                  guestInvites[index] = updated;
                }
                renderRows();
              },
              onError: showError,
            });
          });
          actionsCell.append(editLimitsButton);
        }

        const revokeButton = document.createElement('button');
        revokeButton.type = 'button';
        revokeButton.className = 'button ghost';
        revokeButton.innerHTML = `<span data-i18n="files.owner.revokeGuestInvite"></span>`;
        revokeButton.addEventListener('click', () => {
          void (async () => {
            if (busy) {
              return;
            }

            const confirmed = await showConfirmDialog({
              titleKey: 'files.owner.revokeGuestInviteTitle',
              messageKey: 'files.owner.revokeGuestInviteConfirm',
              confirmLabelKey: 'files.owner.revokeGuestInvite',
              confirmClassName: 'primary',
            });
            if (!confirmed) {
              return;
            }

            busy = true;
            clearError();
            try {
              await api.revokeSharedFolderGuestInvite(folderId, invite.id);
              if (invite.inviteKind === 'QuickShare') {
                guestInvites = guestInvites.filter((entry) => entry.id !== invite.id);
              } else {
                invite.revoked = true;
              }
              renderRows();
            } catch {
              showError(t('status.error'));
            } finally {
              busy = false;
            }
          })();
        });
        actionsCell.append(revokeButton);
      }

      tr.append(nameCell, typeCell, permissionCell, statusCell, budgetCell, actionsCell);
      body.append(tr);
    }

    applyI18n(body);
    const accessTable = panel.querySelector('table.data-table') as HTMLTableElement | null;
    if (accessTable) {
      enhanceDataTableCards(accessTable);
    }
  };

  renderRows();

  panel.querySelector('#files-access-invite-user')?.addEventListener('click', () => {
    void showInviteUserDialog({
      folderId,
      folderKey,
      candidates: inviteCandidates,
      onInvited: (member) => {
        members.push(member);
        memberUserIds.add(member.userId);
        inviteCandidates = inviteCandidates.filter((entry) => entry.userId !== member.userId);
        renderRows();
      },
    });
  });

  panel.querySelector('#files-access-invite-guest')?.addEventListener('click', () => {
    void showInviteGuestDialog({
      folderId,
      folderKey,
      manifest,
      onInvited: (invite) => {
        guestInvites.unshift(invite);
        renderRows();
      },
      onError: showError,
    });
  });

  panel.querySelector('#files-access-quick-share')?.addEventListener('click', () => {
    void showQuickShareDialog({
      folderId,
      folderKey,
      manifest,
      onInvited: (invite) => {
        guestInvites.unshift(invite);
        renderRows();
      },
      onRevoked: (inviteId) => {
        guestInvites = guestInvites.filter((entry) => entry.id !== inviteId);
        renderRows();
      },
      onComplete: () => {
        onQuickShareComplete?.();
      },
      onError: showError,
    });
  });

  return bindLocaleRefresh(() => {
    applyI18n(panel);
    renderRows();
  });
}

interface InviteUserDialogOptions {
  folderId: string;
  folderKey: Uint8Array;
  candidates: KeyDirectoryEntry[];
  onInvited: (member: SharedFolderMember) => void;
}

async function showInviteUserDialog(options: InviteUserDialogOptions): Promise<void> {
  const shell = createModalShell({
    titleKey: 'files.owner.inviteUserTitle',
    helpTopic: dialogHelpTopic('sharedFolderInviteUser'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="files-access-invite-user-form">
      <label>
        <span data-i18n="files.owner.inviteUser"></span>
        <select name="userId" required></select>
      </label>
      <label>
        <span data-i18n="files.owner.permission"></span>
        <select name="permission" required>
          <option value="Read" data-i18n="files.owner.accessRead"></option>
          <option value="ReadWrite" data-i18n="files.owner.accessReadWrite"></option>
          <option value="Full" data-i18n="files.owner.accessFull"></option>
        </select>
      </label>
      <p class="error-text" id="files-access-invite-user-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'files-access-invite-user-cancel';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'files-access-invite-user-form');
  submitButton.setAttribute('data-i18n', 'files.owner.invite');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#files-access-invite-user-form') as HTMLFormElement;
  const userSelect = form.querySelector('select[name="userId"]') as HTMLSelectElement;
  const errorEl = shell.body.querySelector('#files-access-invite-user-error') as HTMLParagraphElement;
  const submitBtn = submitButton;

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.setAttribute('data-i18n', 'files.owner.inviteUserPlaceholder');
  userSelect.append(placeholder);
  for (const candidate of options.candidates) {
    const option = document.createElement('option');
    option.value = candidate.userId;
    option.textContent = candidate.displayName;
    userSelect.append(option);
  }
  applyI18n(userSelect);
  const disabled = options.candidates.length === 0;
  userSelect.disabled = disabled;
  submitBtn.disabled = disabled;

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const userId = String(data.get('userId') ?? '').trim();
      const permission = String(data.get('permission') ?? 'Read') as SharedFolderMemberPermission;
      if (!userId) {
        return;
      }

      submitBtn.disabled = true;
      errorEl.hidden = true;
      try {
        await inviteSharedFolderMember({
          folderId: options.folderId,
          folderKey: options.folderKey,
          userId,
          permission,
        });
        const invited = options.candidates.find((entry) => entry.userId === userId);
        options.onInvited({
          userId,
          displayName: invited?.displayName ?? userId,
          permission,
          createdAt: new Date().toISOString(),
        });
        close();
      } catch (cause) {
        errorEl.hidden = false;
        errorEl.textContent = mapInviteError(cause);
        submitBtn.disabled = disabled;
      }
    })();
  });
}

interface InviteGuestDialogOptions {
  folderId: string;
  folderKey: Uint8Array;
  manifest: SharedFolderManifestPlaintext;
  onInvited: (invite: SharedFolderGuestInviteSummary) => void;
  onError: (message: string) => void;
}

async function showInviteGuestDialog(options: InviteGuestDialogOptions): Promise<void> {
  const shell = createModalShell({
    titleKey: 'files.owner.inviteGuestTitle',
    helpTopic: dialogHelpTopic('sharedFolderInviteGuest'),
  });

  const directoryPaths = options.manifest.entries
    .filter((entry) => entry.type === 'directory')
    .map((entry) => entry.path)
    .sort((a, b) => a.localeCompare(b));

  shell.body.innerHTML = `
    <form class="form-grid" id="files-access-invite-guest-form" method="post" action="#">
      <label>
        <span data-i18n="files.owner.guestInviteLabel"></span>
        <input type="text" name="label" maxlength="200" autocomplete="off" />
        <span class="muted" data-i18n="files.owner.guestInviteLabelHint"></span>
      </label>
      <label>
        <span data-i18n="files.owner.guestRootPath"></span>
        <select name="rootPath">
          <option value="" data-i18n="files.owner.guestRootPathEntire"></option>
          ${directoryPaths.map((path) => `<option value="${path.replace(/"/g, '&quot;')}">${path}</option>`).join('')}
        </select>
        <span class="muted" data-i18n="files.owner.guestRootPathHint"></span>
      </label>
      <label>
        <span data-i18n="files.owner.permission"></span>
        <select name="permission" required>
          <option value="Read" data-i18n="files.owner.accessRead"></option>
          <option value="ReadWrite" data-i18n="files.owner.accessReadWrite"></option>
        </select>
      </label>
      <div class="warning-panel" id="files-access-guest-readwrite-warning" hidden>
        <span class="warning-panel__icon"></span>
        <span data-i18n="files.owner.guestReadWriteWarning"></span>
      </div>
      <div id="files-access-guest-limits" hidden>
        <label>
          <span data-i18n="files.owner.guestUploadMaxFiles"></span>
          <input type="number" name="maxGuestUploadFiles" min="0" step="1" />
          <span class="muted" data-i18n="files.owner.guestUploadMaxFilesHint"></span>
        </label>
        <label>
          <span data-i18n="files.owner.guestUploadMaxSize"></span>
          <input type="number" name="maxGuestUploadTotalMb" min="0" step="0.1" />
          <span class="muted" data-i18n="files.owner.guestUploadMaxSizeHint"></span>
        </label>
      </div>
      <label>
        <span data-i18n="files.owner.guestPassword"></span>
        <input type="password" name="password" autocomplete="new-password" />
        <span class="muted" id="files-access-guest-password-hint" data-i18n="files.owner.guestPasswordHint"></span>
      </label>
      <p class="error-text" id="files-access-invite-guest-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'files-access-invite-guest-cancel';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'files-access-invite-guest-form');
  submitButton.setAttribute('data-i18n', 'files.owner.createGuestInvite');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#files-access-invite-guest-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#files-access-invite-guest-error') as HTMLParagraphElement;
  const submitBtn = submitButton;
  const guestReadWriteWarning = shell.body.querySelector('#files-access-guest-readwrite-warning') as HTMLDivElement;
  const guestLimitsSection = shell.body.querySelector('#files-access-guest-limits') as HTMLDivElement;
  const guestPermissionSelect = form.querySelector('select[name="permission"]') as HTMLSelectElement;
  const guestPasswordInput = form.querySelector('input[name="password"]') as HTMLInputElement;
  const guestPasswordHint = shell.body.querySelector('#files-access-guest-password-hint') as HTMLElement;
  let folderGuestPasswordPolicy = defaultPolicyForTarget('folderGuestPassword');
  void resolvePasswordPolicy('folderGuestPassword').then((policy) => {
    folderGuestPasswordPolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [guestPasswordInput],
      hint: guestPasswordHint,
    });
  });
  guestReadWriteWarning.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));

  const syncGuestReadWriteWarning = () => {
    const isReadWrite = guestPermissionSelect.value === 'ReadWrite';
    guestReadWriteWarning.hidden = !isReadWrite;
    guestLimitsSection.hidden = !isReadWrite;
  };
  guestPermissionSelect.addEventListener('change', syncGuestReadWriteWarning);
  syncGuestReadWriteWarning();

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const permission = String(data.get('permission') ?? 'Read') as SharedFolderGuestPermission;
    const label = String(data.get('label') ?? '');
    const password = String(data.get('password') ?? '');
    const rootPath = normalizeGuestRootPath(String(data.get('rootPath') ?? ''));
    const maxFilesRaw = String(data.get('maxGuestUploadFiles') ?? '').trim();
    const maxSizeRaw = String(data.get('maxGuestUploadTotalMb') ?? '').trim();
    const maxGuestUploadFiles = permission === 'ReadWrite' && maxFilesRaw !== ''
      ? Number(maxFilesRaw)
      : null;
    const maxGuestUploadTotalBytes = permission === 'ReadWrite' && maxSizeRaw !== ''
      ? Math.round(Number(maxSizeRaw) * 1024 * 1024)
      : null;

    if (maxGuestUploadFiles !== null && (!Number.isFinite(maxGuestUploadFiles) || maxGuestUploadFiles < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    if (maxGuestUploadTotalBytes !== null
      && (!Number.isFinite(maxGuestUploadTotalBytes) || maxGuestUploadTotalBytes < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    void (async () => {
      submitBtn.disabled = true;
      errorEl.hidden = true;
      const trimmedPassword = password.trim();
      if (trimmedPassword) {
        const policyMessage = validatePasswordForUi(trimmedPassword, folderGuestPasswordPolicy);
        if (policyMessage) {
          errorEl.hidden = false;
          errorEl.textContent = policyMessage;
          submitBtn.disabled = false;
          return;
        }
      }
      try {
        await showFolderGuestLinkDialog(async () => {
          const created = await createSharedFolderGuestInvite({
            folderId: options.folderId,
            folderKey: options.folderKey,
            permission,
            label,
            password: trimmedPassword || undefined,
            maxGuestUploadFiles,
            maxGuestUploadTotalBytes,
            rootPath: rootPath || undefined,
          });
          options.onInvited({
            id: created.guestInviteId,
            permission,
            label: label.trim() || null,
            revoked: false,
            createdAt: new Date().toISOString(),
            maxGuestUploadFiles,
            maxGuestUploadTotalBytes,
            guestUploadFilesUsed: 0,
            guestUploadBytesUsed: 0,
            rootPath: rootPath || null,
            inviteKind: 'Standard',
          });
          return created;
        });
        close();
      } catch (cause) {
        const message =
          passwordPolicyErrorFromCause(cause, folderGuestPasswordPolicy) ?? mapInviteError(cause);
        errorEl.hidden = false;
        errorEl.textContent = message;
        options.onError(message);
        submitBtn.disabled = false;
      }
    })();
  });
}

interface EditGuestLimitsDialogOptions {
  folderId: string;
  invite: SharedFolderGuestInviteSummary;
  onUpdated: (invite: SharedFolderGuestInviteSummary) => void;
  onError: (message: string) => void;
}

async function showEditGuestLimitsDialog(options: EditGuestLimitsDialogOptions): Promise<void> {
  const shell = createModalShell({
    titleKey: 'files.owner.guestUploadLimitsEditTitle',
    helpTopic: dialogHelpTopic('sharedFolderEditGuest'),
  });

  shell.body.innerHTML = `
    <p class="muted" data-i18n="files.owner.guestUploadLimitsSubtitle"></p>
    <form class="form-grid" id="files-access-edit-guest-limits-form">
      <label>
        <span data-i18n="files.owner.guestUploadMaxFiles"></span>
        <input type="number" name="maxGuestUploadFiles" min="0" step="1" />
        <span class="muted" data-i18n="files.owner.guestUploadMaxFilesHint"></span>
      </label>
      <label>
        <span data-i18n="files.owner.guestUploadMaxSize"></span>
        <input type="number" name="maxGuestUploadTotalMb" min="0" step="0.1" />
        <span class="muted" data-i18n="files.owner.guestUploadMaxSizeHint"></span>
      </label>
      <p class="muted" id="files-access-edit-guest-usage"></p>
      <p class="error-text" id="files-access-edit-guest-limits-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'files-access-edit-guest-limits-cancel';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'files-access-edit-guest-limits-form');
  submitButton.setAttribute('data-i18n', 'files.owner.guestUploadLimitsSave');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#files-access-edit-guest-limits-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#files-access-edit-guest-limits-error') as HTMLParagraphElement;
  const usageEl = shell.body.querySelector('#files-access-edit-guest-usage') as HTMLParagraphElement;
  const submitBtn = submitButton;
  const maxFilesInput = form.querySelector('input[name="maxGuestUploadFiles"]') as HTMLInputElement;
  const maxSizeInput = form.querySelector('input[name="maxGuestUploadTotalMb"]') as HTMLInputElement;

  maxFilesInput.value = typeof options.invite.maxGuestUploadFiles === 'number'
    ? options.invite.maxGuestUploadFiles.toString()
    : '';
  maxSizeInput.value = typeof options.invite.maxGuestUploadTotalBytes === 'number'
    ? (options.invite.maxGuestUploadTotalBytes / (1024 * 1024)).toString()
    : '';

  const usageParts: string[] = [];
  if (typeof options.invite.maxGuestUploadFiles === 'number') {
    usageParts.push(
      `${t('files.owner.guestUploadUsedFiles')}: ${options.invite.guestUploadFilesUsed} / ${options.invite.maxGuestUploadFiles}`,
    );
  }
  if (typeof options.invite.maxGuestUploadTotalBytes === 'number') {
    usageParts.push(
      `${t('files.owner.guestUploadUsedSize')}: ${formatBytes(options.invite.guestUploadBytesUsed)} / ${formatBytes(options.invite.maxGuestUploadTotalBytes)}`,
    );
  }
  usageEl.textContent = usageParts.join(' · ');

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const maxFilesRaw = String(data.get('maxGuestUploadFiles') ?? '').trim();
    const maxSizeRaw = String(data.get('maxGuestUploadTotalMb') ?? '').trim();
    const maxGuestUploadFiles = maxFilesRaw === '' ? null : Number(maxFilesRaw);
    const maxGuestUploadTotalBytes = maxSizeRaw === ''
      ? null
      : Math.round(Number(maxSizeRaw) * 1024 * 1024);

    if (maxGuestUploadFiles !== null && (!Number.isFinite(maxGuestUploadFiles) || maxGuestUploadFiles < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    if (maxGuestUploadTotalBytes !== null
      && (!Number.isFinite(maxGuestUploadTotalBytes) || maxGuestUploadTotalBytes < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    void (async () => {
      submitBtn.disabled = true;
      errorEl.hidden = true;
      try {
        const updated = await api.patchSharedFolderGuestInvite(options.folderId, options.invite.id, {
          maxGuestUploadFiles,
          maxGuestUploadTotalBytes,
        });
        options.onUpdated(updated);
        close();
      } catch (cause) {
        console.error('Save guest upload limits failed', cause);
        errorEl.hidden = false;
        errorEl.textContent = t('status.error');
        options.onError(t('status.error'));
        submitBtn.disabled = false;
      }
    })();
  });
}

export function findActiveQuickShareInvites(
  invites: SharedFolderGuestInviteSummary[],
): SharedFolderGuestInviteSummary[] {
  return invites.filter((invite) => invite.inviteKind === 'QuickShare' && !invite.revoked);
}

interface QuickShareDialogOptions {
  folderId: string;
  folderKey: Uint8Array;
  manifest: SharedFolderManifestPlaintext;
  onInvited: (invite: SharedFolderGuestInviteSummary) => void;
  onRevoked?: (inviteId: string) => void;
  onComplete?: () => void;
  onError: (message: string) => void;
}

async function showQuickShareDialog(options: QuickShareDialogOptions): Promise<void> {
  const shell = createModalShell({
    titleKey: 'files.owner.quickShareTitle',
    helpTopic: dialogHelpTopic('sharedFolderQuickShare'),
  });

  const directoryPaths = options.manifest.entries
    .filter((entry) => entry.type === 'directory')
    .map((entry) => entry.path)
    .sort((a, b) => a.localeCompare(b));

  shell.body.innerHTML = `
    <form class="form-grid" id="files-access-quick-share-form" method="post" action="#">
      <p class="muted" data-i18n="files.owner.quickShareSubtitle"></p>
      <label>
        <span data-i18n="files.owner.guestRootPath"></span>
        <select name="rootPath">
          <option value="" data-i18n="files.owner.guestRootPathEntire"></option>
          ${directoryPaths.map((path) => `<option value="${path.replace(/"/g, '&quot;')}">${path}</option>`).join('')}
        </select>
        <span class="muted" data-i18n="files.owner.guestRootPathHint"></span>
      </label>
      <label>
        <span data-i18n="files.owner.permission"></span>
        <select name="permission" required>
          <option value="ReadWrite" selected data-i18n="files.owner.accessReadWrite"></option>
          <option value="Read" data-i18n="files.owner.accessRead"></option>
        </select>
      </label>
      <div class="warning-panel" id="files-access-quick-share-readwrite-warning" hidden>
        <span class="warning-panel__icon"></span>
        <span data-i18n="files.owner.quickShareReadWriteWarning"></span>
      </div>
      <div id="files-access-quick-share-limits" hidden>
        <label>
          <span data-i18n="files.owner.guestUploadMaxFiles"></span>
          <input type="number" name="maxGuestUploadFiles" min="0" step="1" />
          <span class="muted" data-i18n="files.owner.guestUploadMaxFilesHint"></span>
        </label>
        <label>
          <span data-i18n="files.owner.guestUploadMaxSize"></span>
          <input type="number" name="maxGuestUploadTotalMb" min="0" step="0.1" />
          <span class="muted" data-i18n="files.owner.guestUploadMaxSizeHint"></span>
        </label>
      </div>
      <p class="error-text" id="files-access-quick-share-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'files-access-quick-share-form');
  submitButton.setAttribute('data-i18n', 'files.owner.quickShareStart');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#files-access-quick-share-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#files-access-quick-share-error') as HTMLParagraphElement;
  const guestReadWriteWarning = shell.body.querySelector('#files-access-quick-share-readwrite-warning') as HTMLDivElement;
  const guestLimitsSection = shell.body.querySelector('#files-access-quick-share-limits') as HTMLDivElement;
  const guestPermissionSelect = form.querySelector('select[name="permission"]') as HTMLSelectElement;
  guestReadWriteWarning.querySelector('.warning-panel__icon')?.append(iconImg('warning', 20));

  const syncGuestReadWriteWarning = () => {
    const isReadWrite = guestPermissionSelect.value === 'ReadWrite';
    guestReadWriteWarning.hidden = !isReadWrite;
    guestLimitsSection.hidden = !isReadWrite;
  };
  guestPermissionSelect.addEventListener('change', syncGuestReadWriteWarning);
  syncGuestReadWriteWarning();

  let ownerSession: ReturnType<typeof connectQuickShareOwner> | null = null;
  let dialogClosed = false;
  let suppressDisconnectUi = false;
  let connectGeneration = 0;
  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    dialogClosed = true;
    suppressDisconnectUi = true;
    connectGeneration += 1;
    ownerSession?.close();
    ownerSession = null;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const permission = String(data.get('permission') ?? 'ReadWrite') as SharedFolderGuestPermission;
    const rootPath = normalizeGuestRootPath(String(data.get('rootPath') ?? ''));
    const maxFilesRaw = String(data.get('maxGuestUploadFiles') ?? '').trim();
    const maxSizeRaw = String(data.get('maxGuestUploadTotalMb') ?? '').trim();
    const maxGuestUploadFiles = permission === 'ReadWrite' && maxFilesRaw !== ''
      ? Number(maxFilesRaw)
      : null;
    const maxGuestUploadTotalBytes = permission === 'ReadWrite' && maxSizeRaw !== ''
      ? Math.round(Number(maxSizeRaw) * 1024 * 1024)
      : null;

    if (maxGuestUploadFiles !== null && (!Number.isFinite(maxGuestUploadFiles) || maxGuestUploadFiles < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    if (maxGuestUploadTotalBytes !== null
      && (!Number.isFinite(maxGuestUploadTotalBytes) || maxGuestUploadTotalBytes < 0)) {
      errorEl.hidden = false;
      errorEl.textContent = t('files.owner.guestUploadLimitsInvalid');
      return;
    }

    shell.body.innerHTML = `
      <div class="quick-share-waiting">
        <p class="muted" id="files-quick-share-waiting-hint"></p>
        <p class="quick-share-waiting__code" id="files-quick-share-code">···· - ····</p>
        <p class="muted" id="files-quick-share-status" data-i18n="files.owner.quickShareConnecting"></p>
        <p class="error-text" id="files-quick-share-wait-error" hidden></p>
      </div>
    `;
    shell.actions.replaceChildren();
    const waitCancel = document.createElement('button');
    waitCancel.type = 'button';
    waitCancel.className = 'button ghost';
    waitCancel.setAttribute('data-i18n', 'files.owner.quickShareCancel');
    const restartButton = document.createElement('button');
    restartButton.type = 'button';
    restartButton.className = 'button primary';
    restartButton.setAttribute('data-i18n', 'files.owner.quickShareShowNewCode');
    restartButton.hidden = true;
    shell.actions.append(restartButton, waitCancel);
    applyI18n(shell.dialog);
    waitCancel.addEventListener('click', close);

    const hintEl = shell.body.querySelector('#files-quick-share-waiting-hint') as HTMLElement;
    const shareUrl = `${window.location.origin}/share`;
    hintEl.textContent = t('files.owner.quickShareWaitingHint', {
      url: shareUrl,
      interpolation: { escapeValue: false },
    });
    const codeEl = shell.body.querySelector('#files-quick-share-code') as HTMLElement;
    const statusEl = shell.body.querySelector('#files-quick-share-status') as HTMLElement;
    const waitError = shell.body.querySelector('#files-quick-share-wait-error') as HTMLElement;
    let creating = false;
    let handlingAlreadyActive = false;
    let terminalError = false;

    const showWaitError = (message: string) => {
      waitError.hidden = false;
      waitError.textContent = message;
    };

    const showConnectionLost = () => {
      if (dialogClosed || suppressDisconnectUi || terminalError) {
        return;
      }
      codeEl.textContent = '···· - ····';
      statusEl.removeAttribute('data-i18n');
      statusEl.textContent = t('files.owner.quickShareConnectionLost');
      showWaitError(t('files.owner.quickShareConnectionLostHint'));
      restartButton.hidden = false;
      applyI18n(restartButton);
    };

    const startOwnerSession = () => {
      const myGeneration = ++connectGeneration;
      ownerSession?.close();
      ownerSession = null;
      creating = false;
      terminalError = false;
      waitError.hidden = true;
      waitError.textContent = '';
      restartButton.hidden = true;
      codeEl.textContent = '···· - ····';
      statusEl.setAttribute('data-i18n', 'files.owner.quickShareConnecting');
      applyI18n(statusEl);

      ownerSession = connectQuickShareOwner({
        folderId: options.folderId,
        onSessionCreated: ({ display }) => {
          if (myGeneration !== connectGeneration) return;
          codeEl.textContent = display;
          waitError.hidden = true;
          restartButton.hidden = true;
          statusEl.setAttribute('data-i18n', 'files.owner.quickShareWaitingForGuest');
          applyI18n(statusEl);
        },
        onGuestClaimed: ({ sessionId, guestPublicKey }) => {
          if (myGeneration !== connectGeneration || creating) return;
          creating = true;
          statusEl.setAttribute('data-i18n', 'files.owner.quickShareCreatingInvite');
          applyI18n(statusEl);
          void (async () => {
            try {
              const created = await createSharedFolderGuestInviteMaterials({
                folderId: options.folderId,
                folderKey: options.folderKey,
                permission,
                maxGuestUploadFiles,
                maxGuestUploadTotalBytes,
                rootPath: rootPath || undefined,
                inviteKind: 'QuickShare',
              });
              if (myGeneration !== connectGeneration) return;
              const sealed = await sealQuickShareFragmentPackage({
                sessionId,
                guestPublicKey: decodeBase64Url(guestPublicKey),
                fragment: created.fragment,
                guestPath: created.guestPath,
              });
              if (myGeneration !== connectGeneration) return;
              ownerSession?.sendPackage({
                sessionId,
                guestPath: sealed.guestPath,
                wrappedPackageKey: sealed.wrappedPackageKey,
                ciphertext: sealed.ciphertext,
              });
              options.onInvited({
                id: created.guestInviteId,
                permission,
                label: null,
                revoked: false,
                createdAt: new Date().toISOString(),
                maxGuestUploadFiles,
                maxGuestUploadTotalBytes,
                guestUploadFilesUsed: 0,
                guestUploadBytesUsed: 0,
                rootPath: rootPath || null,
                inviteKind: 'QuickShare',
              });
            } catch (cause) {
              if (myGeneration !== connectGeneration) return;
              console.error('quick share invite failed', cause);
              if (
                cause instanceof ApiError
                && cause.code === 'quick_share_already_active'
              ) {
                creating = false;
                void handleAlreadyActive();
                return;
              }
              terminalError = true;
              showWaitError(mapInviteError(cause));
              options.onError(mapInviteError(cause));
              creating = false;
            }
          })();
        },
        onPackageDelivered: () => {
          if (myGeneration !== connectGeneration) return;
          suppressDisconnectUi = true;
          statusEl.setAttribute('data-i18n', 'files.owner.quickShareDone');
          applyI18n(statusEl);
          window.setTimeout(() => {
            close();
            options.onComplete?.();
          }, 800);
        },
        onCancelled: (reason) => {
          if (myGeneration !== connectGeneration || dialogClosed || suppressDisconnectUi || terminalError) {
            return;
          }
          if (reason === 'owner_disconnected') {
            showConnectionLost();
            return;
          }
          terminalError = true;
          showWaitError(t('files.owner.quickShareCancelled'));
          restartButton.hidden = false;
          applyI18n(restartButton);
        },
        onError: (code) => {
          if (myGeneration !== connectGeneration) return;
          if (code === 'quick_share_already_active') {
            void handleAlreadyActive();
            return;
          }
          terminalError = true;
          showWaitError(
            code === 'session_limit'
              ? t('files.owner.quickShareSessionLimit')
              : code === 'folder_session_active'
                ? t('files.owner.quickShareFolderSessionActive')
                : t('status.error'),
          );
        },
        onStateChange: (state) => {
          if (myGeneration !== connectGeneration) return;
          if (state === 'disconnected') {
            showConnectionLost();
          }
        },
      });
    };

    const handleAlreadyActive = async () => {
      if (handlingAlreadyActive || dialogClosed) {
        return;
      }
      handlingAlreadyActive = true;
      terminalError = true;
      try {
        const confirmed = await showConfirmDialog({
          titleKey: 'files.owner.quickShareAlreadyActiveTitle',
          messageKey: 'files.owner.quickShareAlreadyActiveConfirm',
          confirmLabelKey: 'files.owner.quickShareAlreadyActiveRevoke',
          confirmClassName: 'primary',
        });
        if (dialogClosed) {
          return;
        }
        if (!confirmed) {
          showWaitError(t('files.owner.quickShareAlreadyActive'));
          return;
        }
        statusEl.removeAttribute('data-i18n');
        statusEl.textContent = t('files.owner.quickShareRevoking');
        try {
          const invites = await api.listSharedFolderGuestInvites(options.folderId);
          const active = findActiveQuickShareInvites(invites);
          for (const invite of active) {
            await api.revokeSharedFolderGuestInvite(options.folderId, invite.id);
            options.onRevoked?.(invite.id);
          }
          if (!dialogClosed) {
            startOwnerSession();
          }
        } catch (cause) {
          console.error('quick share revoke failed', cause);
          showWaitError(t('status.error'));
        }
      } finally {
        handlingAlreadyActive = false;
      }
    };

    restartButton.addEventListener('click', () => {
      startOwnerSession();
    });

    startOwnerSession();
  });
}

